'use strict';

/**
 * Primitives de parsing / formatage des nombres au format suisse.
 *
 * Source unique côté JS (miroir de SwissNumberFormatHelper côté PHP) :
 *   - milliers : apostrophe « ' »
 *   - décimal : point « . » (montants), aucune décimale pour les km
 *
 * Contrat de glyphes tolérés à la saisie (copier-coller macOS, toLocaleString) :
 *   - apostrophes : U+0027 (droite) et U+2019 (typographique)
 *   - espaces : U+0020, U+00A0 (insécable), U+202F (fine insécable), U+2009 (fine)
 *   - séparateur décimal : « . » ou « , » (normalisé vers « . »)
 *
 * Formatage live (saisie) — fixtures de référence :
 * | raw        | moneyLive | integerLive |
 * | 12500      | 12'500    | 12'500      |
 * | 1'234.     | 1'234.    | null        |
 * | 1234.5     | 1'234.5   | null        |
 * | 1234.567   | null      | null        |
 * | 12a        | null      | null        |
 * | (vide)     | (vide)    | (vide)      |
 * | 12'        | 12        | 12          |
 */

// Expression des glyphes de séparateurs à retirer avant conversion.
const SEPARATOR_GLYPHS_REGEX = /['\u2019\u0020\u00A0\u202F\u2009]/g;

/**
 * Parse un montant CHF au format suisse ou canonique.
 *
 * @param {string|number|null|undefined} raw
 * @param {boolean} [allowNegative=false] Autorise un signe moins (équipements).
 * @returns {number|null} Montant arrondi à 2 décimales, null si vide ou invalide.
 */
export function parseSwissMoney(raw, allowNegative = false) {
    if (raw === null || raw === undefined) {
        return null;
    }

    const cleaned = String(raw)
        .trim()
        .replace(SEPARATOR_GLYPHS_REGEX, '')
        .replace(',', '.');

    const pattern = allowNegative ? /^-?\d+(\.\d{1,2})?$/ : /^\d+(\.\d{1,2})?$/;
    if (cleaned === '' || !pattern.test(cleaned)) {
        return null;
    }

    const value = Number(cleaned);
    return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
}

/**
 * Parse un entier (km) au format suisse ou canonique.
 *
 * @param {string|number|null|undefined} raw
 * @returns {number|null} Entier, null si vide ou invalide.
 */
export function parseSwissInteger(raw) {
    if (raw === null || raw === undefined) {
        return null;
    }

    const cleaned = String(raw).trim().replace(SEPARATOR_GLYPHS_REGEX, '');
    if (cleaned === '' || !/^\d+$/.test(cleaned)) {
        return null;
    }

    const value = Number(cleaned);
    return Number.isFinite(value) ? Math.trunc(value) : null;
}

/**
 * Groupe la partie entière avec l'apostrophe suisse (sans signe).
 *
 * @param {string} integerDigits Chiffres uniquement (pas de « - »).
 * @returns {string}
 */
function groupThousands(integerDigits) {
    return integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '\'');
}

/**
 * Formate un montant CHF (apostrophe milliers, point décimal).
 *
 * @param {number|null} value
 * @param {boolean} [trimZeroDecimals=false] Affiche un entier si la partie
 *                                           décimale est nulle ; sinon 2 décimales.
 * @returns {string} Montant formaté, chaîne vide si null.
 */
export function formatSwissMoney(value, trimZeroDecimals = false) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return '';
    }

    const negative = value < 0;
    const absolute = Math.abs(value);

    if (trimZeroDecimals && Math.abs(absolute - Math.round(absolute)) < 1e-9) {
        const grouped = groupThousands(String(Math.round(absolute)));
        return negative ? '-' + grouped : grouped;
    }

    const parts = absolute.toFixed(2).split('.');
    parts[0] = groupThousands(parts[0]);
    const formatted = parts.join('.');

    return negative ? '-' + formatted : formatted;
}

/**
 * Formate un entier (km) avec l'apostrophe milliers.
 *
 * @param {number|null} value
 * @returns {string} Entier formaté, chaîne vide si null.
 */
export function formatSwissInteger(value) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return '';
    }

    return groupThousands(String(Math.trunc(value)));
}

/**
 * Indique si un caractère compte pour le positionnement du caret
 * (chiffres + un seul séparateur décimal « . »).
 *
 * @param {string} char
 * @returns {boolean}
 */
function isSignificantChar(char) {
    // Virgule comptée avant normalisation live (virgule → point).
    // Le signe « - » compte pour le caret sur les montants signés.
    return (char >= '0' && char <= '9') || char === '.' || char === ',' || char === '-';
}

/**
 * Compte les caractères significatifs (chiffres + décimal) avant le caret.
 * Les apostrophes et espaces de milliers sont ignorés.
 *
 * @param {string} value
 * @param {number} caretPos
 * @returns {number}
 */
export function countSignificantCharsBeforeCaret(value, caretPos) {
    const text = String(value ?? '');
    const end = Math.max(0, Math.min(Number(caretPos) || 0, text.length));
    let count = 0;

    for (let i = 0; i < end; i += 1) {
        if (isSignificantChar(text.charAt(i))) {
            count += 1;
        }
    }

    return count;
}

/**
 * Positionne le caret après le n-ième caractère significatif dans la valeur formatée.
 *
 * @param {string} formattedValue
 * @param {number} significantCount
 * @returns {number}
 */
export function mapCaretAfterFormat(formattedValue, significantCount) {
    const text = String(formattedValue ?? '');
    const target = Math.max(0, Number(significantCount) || 0);

    if (target === 0) {
        return 0;
    }

    let seen = 0;
    for (let i = 0; i < text.length; i += 1) {
        if (isSignificantChar(text.charAt(i))) {
            seen += 1;
            if (seen >= target) {
                return i + 1;
            }
        }
    }

    return text.length;
}

/**
 * Formatage live d'un montant pendant la frappe (sans toFixed).
 * Retourne null si invalide → laisser la chaîne inchangée.
 *
 * @param {string|number|null|undefined} raw
 * @param {boolean} [allowNegative=false]
 * @returns {string|null}
 */
export function formatSwissMoneyLive(raw, allowNegative = false) {
    if (raw === null || raw === undefined) {
        return null;
    }

    const trimmed = String(raw).trim();
    if (trimmed === '') {
        return '';
    }

    // Normalise virgule → point pour le modèle interne, retire les milliers.
    let cleaned = trimmed.replace(SEPARATOR_GLYPHS_REGEX, '').replace(',', '.');
    let sign = '';
    if (allowNegative && cleaned.charAt(0) === '-') {
        sign = '-';
        cleaned = cleaned.slice(1);
    }

    // Saisie en cours du seul signe moins.
    if (allowNegative && sign === '-' && cleaned === '') {
        return '-';
    }

    // Entier seul, ou entier + point (transitoire), ou entier + 1–2 décimales.
    const match = cleaned.match(/^(\d+)(\.(\d{0,2})?)?$/);
    if (!match) {
        return null;
    }

    const integerPart = match[1];
    const hasDecimalSeparator = match[2] !== undefined;
    const decimalDigits = match[3] !== undefined ? match[3] : '';

    // Plus de 2 décimales déjà exclu par la regex ; garde-fou.
    if (decimalDigits.length > 2) {
        return null;
    }

    let result = groupThousands(integerPart);
    if (hasDecimalSeparator) {
        result += '.' + decimalDigits;
    }

    return sign + result;
}

/**
 * Formatage live d'un entier (km / filtres) pendant la frappe.
 * Retourne null si décimal ou caractères non numériques.
 *
 * @param {string|number|null|undefined} raw
 * @returns {string|null}
 */
export function formatSwissIntegerLive(raw) {
    if (raw === null || raw === undefined) {
        return null;
    }

    const trimmed = String(raw).trim();
    if (trimmed === '') {
        return '';
    }

    const cleaned = trimmed.replace(SEPARATOR_GLYPHS_REGEX, '');
    // Point / virgule = invalide pour un entier live (ne pas corriger silencieusement).
    if (/[.,]/.test(cleaned) || !/^\d+$/.test(cleaned)) {
        return null;
    }

    return groupThousands(cleaned);
}

export default {
    parseSwissMoney,
    parseSwissInteger,
    formatSwissMoney,
    formatSwissInteger,
    countSignificantCharsBeforeCaret,
    mapCaretAfterFormat,
    formatSwissMoneyLive,
    formatSwissIntegerLive,
};
